import { z } from 'zod';
import { request } from '@/lib/api';

export const CONTRACTS_KEY = ['contracts'] as const;

/** סוגי חוזים. התוויות ב-contracts:categories.* — מתורגמות בתצוגה. */
export const CATEGORIES = ['printers', 'internet', 'phones', 'food', 'water', 'cleaning', 'maintenance', 'insurance', 'software', 'rent', 'other'] as const;
export type Category = (typeof CATEGORIES)[number];
export const isCategory = (c: string | null | undefined): c is Category => !!c && (CATEGORIES as readonly string[]).includes(c);

export const contractSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  category: z.string(),
  startDate: z.string().nullable(),
  endDate: z.string().nullable(),
  autoRenew: z.boolean(),
  noticeDays: z.number(),
  cost: z.string().nullable(),
  costPeriod: z.enum(['MONTHLY', 'YEARLY', 'ONE_TIME']).nullable(),
  warrantyUntil: z.string().nullable(),
  notes: z.string().nullable(),
  /** עיקרי החוזה בעברית — נקרא (ותורגם) מהקובץ. */
  summary: z.string().nullable().catch(null),
  fileName: z.string().nullable().catch(null),
  hasFile: z.boolean().catch(false),
  supplierId: z.string().uuid().nullable(),
  supplier: z.object({ id: z.string(), name: z.string(), phone: z.string().nullish(), email: z.string().nullish(), contactName: z.string().nullish() }).nullable(),
  noticeDate: z.string().nullable(),
  state: z.enum(['active', 'decide_soon', 'notice_passed', 'expired', 'open_ended']),
});
export type Contract = z.infer<typeof contractSchema>;

export interface ContractInput {
  title: string;
  category: string;
  supplierId: string | null;
  startDate: string | null;
  endDate: string | null;
  autoRenew: boolean;
  noticeDays: number;
  cost: number | null;
  costPeriod: 'MONTHLY' | 'YEARLY' | 'ONE_TIME' | null;
  warrantyUntil: string | null;
  notes: string | null;
  summary: string | null;
}

const nul = <T extends z.ZodTypeAny>(t: T) => t.nullable().catch(null);
export const extractionSchema = z.object({
  extracted: z.object({
    title: nul(z.string()),
    category: nul(z.string()),
    supplierName: nul(z.string()),
    supplierTaxId: nul(z.string()),
    supplierEmail: nul(z.string()),
    supplierPhone: nul(z.string()),
    contactName: nul(z.string()),
    startDate: nul(z.string()),
    endDate: nul(z.string()),
    autoRenew: nul(z.boolean()),
    noticeDays: nul(z.number()),
    cost: nul(z.number()),
    costPeriod: nul(z.enum(['MONTHLY', 'YEARLY', 'ONE_TIME'])),
    warrantyUntil: nul(z.string()),
    summary: nul(z.string()),
    language: nul(z.string()),
    confidence: z.number().catch(0),
  }),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable(),
  source: z.enum(['ai', 'text', 'none']),
  warnings: z.array(z.string()),
});
export type ContractExtraction = z.infer<typeof extractionSchema>;

export const CONTRACT_FILE_MAX = 10 * 1024 * 1024;
export const CONTRACT_FILE_ACCEPT = '.pdf,application/pdf,image/jpeg,image/png,image/webp';

export const contractsApi = {
  list: (signal?: AbortSignal) => request('/contracts', { schema: z.array(contractSchema), signal }),
  create: (v: ContractInput) => request('/contracts', { method: 'POST', body: v, schema: contractSchema }),
  update: (id: string, v: ContractInput) => request(`/contracts/${id}`, { method: 'PATCH', body: v, schema: contractSchema }),
  remove: (id: string) => request(`/contracts/${id}`, { method: 'DELETE' }),
  /** קובץ → השדות לטופס. לא שומר. */
  extract: (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request('/contracts/extract', { method: 'POST', body, schema: extractionSchema });
  },
  attachFile: (id: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request(`/contracts/${id}/file`, { method: 'POST', body, schema: contractSchema });
  },
};

export const monthlyCost = (c: Pick<Contract, 'cost' | 'costPeriod'>) =>
  c.cost == null ? 0 : c.costPeriod === 'MONTHLY' ? Number(c.cost) : c.costPeriod === 'YEARLY' ? Number(c.cost) / 12 : 0;
